/**
 * WritingPracticePage — the gated /writing-practice page.
 *
 * Implements show-not-tell/TS-004 (T5) and show-not-tell/US-004 (T6).
 * Hosts the "Start practice" primary button, restores an active session on
 * mount (reload path, AC-TS004.4 / AC-004.4), and once a session is active
 * drops into the CoachingConversation shell wired to useWritingSession.
 *
 * Entirely separate from the comprehension /sessions page — no shared
 * routing or state (BR-TS004.2 / BR-009 slice isolation). Reuses the
 * MathCoachPage page-scaffold conventions (min-h-screen container, header,
 * useNavigate + ROUTES) and the shared Button component.
 *
 * Registered at ROUTES.WRITING_PRACTICE, nested under AuthGuard >
 * FeatureGuard (see App.tsx / show-not-tell/TS-004 T6).
 *
 * show-not-tell/US-011 (AC-011.2): also supports opening a SPECIFIC owned
 * session via the `?sessionId=` query param (WritingPracticeTile navigates
 * here with the tapped row's id, TO-LL-013 — useSearchParams, never manual
 * string parsing). This closes the gap TS-012's dev flagged: prior to
 * US-011 the tile only ever navigated to the bare start screen, so a
 * completed session could never actually be reopened/reviewed from the
 * dashboard even though GET {id} (API-006) already returned its full
 * transcript. When `?sessionId=` is present, openSession() is called
 * instead of restore() (the sessionStorage-persisted-active-session path);
 * the two are mutually exclusive on mount.
 */

import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CoachingConversation } from '@/components/CoachingConversation';
import { useWritingSession } from '@/hooks/useWritingSession';
// BUG-20260717-003154 (RC-004, T-6): source the student's custom profile
// photo the same way the comprehension flow does (SessionChatPage.tsx),
// so the writing-practice chat shows a real student avatar when set.
import { usePebbles } from '@/hooks/usePebbles';
import { ROUTES } from '@/constants/routes';

export function WritingPracticePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const session = useWritingSession();
  const { profile } = usePebbles();
  // show-not-tell/US-011 (AC-011.2, TO-LL-013): the specific session id the
  // tile asked to reopen, if any.
  const requestedSessionId = searchParams.get('sessionId');

  // Reload restore (AC-TS004.4 / AC-004.4) OR open a specific requested
  // session (show-not-tell/US-011 / AC-011.2): on mount, if the tile passed
  // a `?sessionId=`, open that exact owned session; otherwise fall back to
  // the pre-existing persisted-active-session restore path unchanged. Never
  // starts a new session on either path.
  useEffect(() => {
    if (requestedSessionId) {
      session.openSession(requestedSessionId);
    } else {
      session.restore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedSessionId]);

  // 404 mid-session (Gate B lost / session unknown) -> navigate to dashboard,
  // never render a raw error.
  useEffect(() => {
    if (session.error?.kind === 'close') {
      navigate(ROUTES.STUDENT_HOME);
    }
  }, [session.error, navigate]);

  // show-not-tell/SPRINT-5-ISS-002: detect when the backend reaches
  // phase='close' (TS-009's loop termination or natural PASS / reveal-at-max)
  // and automatically complete the session, transitioning the UI to the
  // completion screen (win summary + report). The complete() function is
  // idempotent (exists at hook line 353, already works correctly when called
  // directly); this wiring simply ensures it is actually invoked from the UI.
  useEffect(() => {
    if (session.phase === 'close' && session.status === 'active') {
      session.complete();
    }
  }, [session.phase, session.status, session]);

  const handleCancel = async () => {
    await session.cancel();
    navigate(ROUTES.STUDENT_HOME);
  };

  return (
    <div className="min-h-screen flex flex-col bg-otter-paper">
      <header className="border-b border-[#E7D8C8] bg-otter-paper px-3 md:px-4 py-2 md:py-3 flex items-center justify-between sticky top-0 z-10 shadow-sm">
        <button
          type="button"
          onClick={() => navigate(ROUTES.STUDENT_HOME)}
          aria-label="Back to dashboard"
          className="text-sm text-otter-earth-500 hover:text-otter-earth-700 flex items-center gap-0.5 min-h-11 min-w-11 justify-center"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Back</span>
        </button>
        <h1 className="text-sm font-medium text-otter-earth-700">Show, don&apos;t tell</h1>
        {session.status === 'active' ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-11 md:h-9"
            aria-label="End practice"
            onClick={handleCancel}
          >
            End
          </Button>
        ) : (
          <span className="w-11" />
        )}
      </header>

      {(session.isRestoring || session.isOpeningSession) && (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-otter-orange-500" />
        </div>
      )}

      {!session.isRestoring && !session.isOpeningSession && session.status === 'idle' && (
        <div className="flex flex-1 flex-col items-center justify-center px-6 text-center gap-4">
          <h2 className="text-xl font-semibold text-otter-earth-800">
            Turn telling sentences into vivid showing ones
          </h2>
          <p className="text-sm text-otter-earth-600 max-w-sm">
            Your coach will help you find the perfect words, step by step.
          </p>
          <Button
            type="button"
            variant="warm"
            size="lg"
            className="h-12"
            aria-label="Start practice"
            disabled={session.isStarting}
            onClick={() => session.start()}
          >
            {session.isStarting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Start practice
          </Button>
          {session.error && session.error.kind === 'retry' && (
            <p role="alert" className="text-sm text-otter-earth-700">
              {session.error.message}
            </p>
          )}
        </div>
      )}

      {!session.isRestoring && !session.isOpeningSession && session.status === 'active' && (
        <CoachingConversation
          messages={session.messages}
          isSending={session.isSending}
          error={session.error}
          onSend={session.sendTurn}
          onRetry={session.retry}
          onClose={() => navigate(ROUTES.STUDENT_HOME)}
          profileImageUrl={profile?.profileImageUrl}
        />
      )}

      {!session.isRestoring && !session.isOpeningSession && session.status === 'completed' && (
        <div className="flex flex-1 flex-col overflow-hidden">
          {/*
            show-not-tell/US-011 (AC-011.2): render the full transcript
            read-only so a REOPENED historical session actually "renders the
            conversation" per the AC's own then-clause, not just a bare
            summary screen. Renders identically for a session that just
            completed in this same page load (messages already present in
            state) and for one reopened via ?sessionId= from the tile.
          */}
          {session.messages.length > 0 && (
            <CoachingConversation
              messages={session.messages}
              isSending={false}
              onSend={session.sendTurn}
              onRetry={session.retry}
              onClose={() => navigate(ROUTES.STUDENT_HOME)}
              profileImageUrl={profile?.profileImageUrl}
              readOnly
            />
          )}
          <div className="flex flex-col items-center justify-center px-6 py-6 text-center gap-4 border-t border-otter-sand">
            <h2 className="text-xl font-semibold text-otter-earth-800">Nice work!</h2>
            {session.report?.summary && (
              <p className="text-sm text-otter-earth-700 max-w-sm">{session.report.summary}</p>
            )}
            <Button
              type="button"
              variant="warm"
              size="lg"
              className="h-12"
              aria-label="Back to dashboard"
              onClick={() => navigate(ROUTES.STUDENT_HOME)}
            >
              Back to dashboard
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
